import type { CSSProperties } from 'react';

interface AddressData {
  attention: string;
  address: string;
  street2: string;
  suburb: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface AddressFormData {
  billing_address: AddressData;
  shipping_address: AddressData;
  same_shipping: boolean;
  [key: string]: unknown;
}

interface CustomerFormAddressSectionProps {
  expanded: boolean;
  onToggle: () => void;
  formData: AddressFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddressChange: (addressType: string, field: string, value: string) => void;
  styles: Record<string, CSSProperties>;
}

export default function CustomerFormAddressSection({
  expanded,
  onToggle,
  formData,
  onChange,
  onAddressChange,
  styles,
}: CustomerFormAddressSectionProps) {
  return (
    <div style={styles.section}>
      <div
        style={styles.collapsibleHeader}
        onClick={onToggle}
      >
        <span style={styles.sectionTitle}>
          {expanded ? '▼' : '▶'} Address
        </span>
        <span style={styles.expandHint}>
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </div>

      {expanded && (
        <div style={styles.collapsibleContent}>
          <div style={styles.addressLabel}>Billing Address</div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Attention</label>
            <input
              type="text"
              style={styles.input}
              value={formData.billing_address.attention}
              onChange={(e) => onAddressChange('billing_address', 'attention', e.target.value)}
              placeholder="Attention to..."
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Street Address</label>
            <input
              type="text"
              style={styles.input}
              value={formData.billing_address.address}
              onChange={(e) => onAddressChange('billing_address', 'address', e.target.value)}
              placeholder="Street address"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Street 2</label>
            <input
              type="text"
              style={styles.input}
              value={formData.billing_address.street2}
              onChange={(e) => onAddressChange('billing_address', 'street2', e.target.value)}
              placeholder="Apartment, suite, etc."
            />
          </div>

          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Suburb</label>
              <input
                type="text"
                style={styles.input}
                value={formData.billing_address.suburb}
                onChange={(e) => onAddressChange('billing_address', 'suburb', e.target.value)}
                placeholder="Suburb"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>City</label>
              <input
                type="text"
                style={styles.input}
                value={formData.billing_address.city}
                onChange={(e) => onAddressChange('billing_address', 'city', e.target.value)}
                placeholder="City"
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>State/Region</label>
              <input
                type="text"
                style={styles.input}
                value={formData.billing_address.state}
                onChange={(e) => onAddressChange('billing_address', 'state', e.target.value)}
                placeholder="State or region"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Postal Code</label>
              <input
                type="text"
                style={styles.input}
                value={formData.billing_address.zip}
                onChange={(e) => onAddressChange('billing_address', 'zip', e.target.value)}
                placeholder="Postal code"
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Country</label>
              <input
                type="text"
                style={styles.input}
                value={formData.billing_address.country}
                onChange={(e) => onAddressChange('billing_address', 'country', e.target.value)}
                placeholder="Country"
              />
            </div>
          </div>

          <div style={styles.checkboxRow}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="same_shipping"
                checked={formData.same_shipping}
                onChange={onChange}
              />
              <span>Shipping address same as billing</span>
            </label>
          </div>

          {!formData.same_shipping && (
            <>
              <div className="mt-md" style={styles.addressLabel}>Shipping Address</div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Attention</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.shipping_address.attention}
                  onChange={(e) => onAddressChange('shipping_address', 'attention', e.target.value)}
                  placeholder="Attention to..."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Street Address</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.shipping_address.address}
                  onChange={(e) => onAddressChange('shipping_address', 'address', e.target.value)}
                  placeholder="Street address"
                />
              </div>

              <div style={styles.row}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Suburb</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.shipping_address.suburb}
                    onChange={(e) => onAddressChange('shipping_address', 'suburb', e.target.value)}
                    placeholder="Suburb"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>City</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.shipping_address.city}
                    onChange={(e) => onAddressChange('shipping_address', 'city', e.target.value)}
                    placeholder="City"
                  />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Postal Code</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.shipping_address.zip}
                    onChange={(e) => onAddressChange('shipping_address', 'zip', e.target.value)}
                    placeholder="Postal code"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Country</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.shipping_address.country}
                    onChange={(e) => onAddressChange('shipping_address', 'country', e.target.value)}
                    placeholder="Country"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
